import { IPlayerAdapter, PlayerInfo, VideoMetadataType, PlatformType } from '@/services/conviva/conviva.types';
import ConvivaHtml5Module from '@convivainc/conviva-js-html5';
import Conviva, { VideoAnalytics } from '@convivainc/conviva-js-coresdk';
import shaka from 'shaka-player';
import { deviceDetector } from '@/services/conviva/device-detector';
import { detectPlatform } from '@/utils/device';
import packageJson from '../../../../package.json';

function getPlatformPlayerName(): string {
  const platform = deviceDetector.deviceMetadata.platform;
  switch (platform) {
    case PlatformType.Tizen:
      return 'Thmanyah_Tizen';
    case PlatformType.WebOS:
      return 'Thmanyah_WebOS';
    case PlatformType.Vidaa:
      return 'Thmanyah_Vidaa';
    case PlatformType.Web:
    default:
      return 'Thmanyah_TV_WEB_EMULATOR';
  }
}

export class ShakaAdapter implements IPlayerAdapter {
  player = null;
  videoAnalytics: VideoAnalytics | null = null;
  private cleanUpCallbacks: Array<() => void> = [];
  private logging = false;

  private _avgBrWeightedSum = 0;
  private _avgBrTotalMs = 0;
  private _avgBrIntervalKbps = 0;
  private _avgBrIntervalTs = 0;

  private _resetAvgBitrate() {
    this._avgBrWeightedSum = 0;
    this._avgBrTotalMs = 0;
    this._avgBrIntervalKbps = 0;
    this._avgBrIntervalTs = 0;
  }

  private _reportBitrate(kbps: number) {
    if (!this.videoAnalytics || kbps <= 0) return;
    const nowMs = Date.now();
    if (this._avgBrIntervalTs > 0 && this._avgBrIntervalKbps > 0) {
      const elapsed = nowMs - this._avgBrIntervalTs;
      this._avgBrWeightedSum += this._avgBrIntervalKbps * elapsed;
      this._avgBrTotalMs += elapsed;
    }
    this._avgBrIntervalKbps = kbps;
    this._avgBrIntervalTs = nowMs;
    this.videoAnalytics.reportPlaybackMetric(Conviva.Constants.Playback.BITRATE, kbps);
    const avgKbps = this._avgBrTotalMs > 0 ? Math.round(this._avgBrWeightedSum / this._avgBrTotalMs) : kbps;
    this.videoAnalytics.reportPlaybackMetric(Conviva.Constants.Playback.AVG_BITRATE, avgKbps);
  }

  async createSession({
    assetName = 'NA',
    viewerId,
    isLive = false,
  }: {
    assetName?: string;
    viewerId?: string;
    isLive?: boolean;
  }) {
    await deviceDetector.ready;
    if (process.env.NODE_ENV !== 'production') {
      const platform = typeof window !== 'undefined' ? detectPlatform() : 'web';
      const isTvPlatform = ['webos', 'tizen', 'vidaa'].includes(platform);
      const usingTestGateway = !!(process.env.VUE_APP_CONVIVA_GATEWAY_URL || '').trim();
      if (!isTvPlatform && usingTestGateway) {
        if (this.logging) {
          console.warn('[conviva][shaka] Skipping Conviva session: non-TV + test gateway');
        }
        return;
      }
    }

    try {
      const trimmedName = assetName?.trim() || '';
      let validAssetName = trimmedName;
      if (!trimmedName || trimmedName.toUpperCase() === 'NA') {
        console.warn(`[conviva][ShakaAdapter] Invalid assetName: "${assetName}". Using fallback.`);
        validAssetName = 'Unknown Asset';
      }

      const settings: {
        [Conviva.Constants.GATEWAY_URL]?: string;
        [Conviva.Constants.LOG_LEVEL]?: number;
      } = {};

      if (process.env.VUE_APP_CONVIVA_GATEWAY_URL) {
        settings[Conviva.Constants.GATEWAY_URL] = process.env.VUE_APP_CONVIVA_GATEWAY_URL || '';
      }

      if (this.logging) {
        settings[Conviva.Constants.LOG_LEVEL] = Conviva.Constants.LogLevel.DEBUG;
      }

      Conviva.Analytics.init(process.env.VUE_APP_CONVIVA_CUSTOMER_KEY || '', null, settings);

      Conviva.Analytics.reportDeviceMetric(
        Conviva.Constants.Network.CONNECTION_TYPE,
        deviceDetector.deviceMetadata.connectionType || 'WiFi',
      );

      Conviva.Analytics.setDeviceMetadata({
        [Conviva.Constants.DeviceMetadata.BRAND]: deviceDetector.deviceMetadata.brand,
        [Conviva.Constants.DeviceMetadata.MANUFACTURER]: deviceDetector.deviceMetadata.manufacturer,
        [Conviva.Constants.DeviceMetadata.MODEL]: deviceDetector.deviceMetadata.deviceModel,
        [Conviva.Constants.DeviceMetadata.TYPE]: Conviva.Constants.DeviceType.SMARTTV,
        [Conviva.Constants.DeviceMetadata.OS_NAME]: deviceDetector.deviceMetadata.osName,
        [Conviva.Constants.DeviceMetadata.OS_VERSION]: deviceDetector.deviceMetadata.osVersion,
        [Conviva.Constants.DeviceMetadata.CATEGORY]:
          deviceDetector.deviceMetadata.platform === PlatformType.Web
            ? Conviva.Constants.DeviceCategory.WEB
            : Conviva.Constants.DeviceCategory.SMART_TV,
      });

      this.videoAnalytics = Conviva.Analytics.buildVideoAnalytics();

      // Set framework info before reportPlaybackRequested so it is included in the opening heartbeat
      this.videoAnalytics.setPlayerInfo({
        [Conviva.Constants.FRAMEWORK_NAME]: 'ShakaPlayer',
        [Conviva.Constants.FRAMEWORK_VERSION]: shaka.Player.version,
      });

      this.videoAnalytics.reportPlaybackRequested({
        [Conviva.Constants.ASSET_NAME]: validAssetName,
        [Conviva.Constants.VIEWER_ID]: viewerId || 'NA',
        [Conviva.Constants.IS_LIVE]: isLive ? 'LIVE' : 'VOD',
        [Conviva.Constants.APPLICATION_VERSION]: packageJson.version,
        [Conviva.Constants.PLAYER_NAME]: getPlatformPlayerName(),
        [Conviva.Constants.FRAMEWORK_NAME]: 'ShakaPlayer',
        [Conviva.Constants.FRAMEWORK_VERSION]: shaka.Player.version,
      });
    } catch (error) {
      if (this.logging) {
        console.warn('[conviva][shaka] Failed to initialize Conviva session:', error);
      }
      this.videoAnalytics = null;
    }
  }

  destroySession() {
    if (!this.videoAnalytics) {
      return;
    }
    this._resetAvgBitrate();
    this.videoAnalytics.release();
    this.videoAnalytics = null;
    this.cleanUpCallbacks.forEach(callback => callback());
    this.cleanUpCallbacks = [];
  }

  setPlayer(player: any) {
    if (!this.videoAnalytics) {
      return;
    }
    this.player = player;

    setTimeout(() => {
      if (!this.videoAnalytics) return;
      this.videoAnalytics.setPlayer(player.getMediaElement(), {
        [Conviva.Constants.CONVIVA_MODULE]: ConvivaHtml5Module,
      });
    }, 0);

    const normalizeErrorMessage = (err: unknown) => {
      if (err instanceof Error) return err.message;
      if (typeof err === 'string') return err;
      try {
        return JSON.stringify(err);
      } catch {
        return String(err);
      }
    };

    const onError = (error: any) => {
      if (!this.videoAnalytics) return;
      if (error?.detail instanceof Error) {
        this.videoAnalytics.reportPlaybackError(error.detail.message);
        return;
      }
      if (error?.detail?.severity === shaka.util.Error.Severity.CRITICAL) {
        this.videoAnalytics.reportPlaybackError(error.detail?.message || normalizeErrorMessage(error.detail || error));
      }
    };

    const bitsToKbps = (bps: number) => Math.max(0, Math.round(bps / 1000));

    const onQualityChanged = (event: any) => {
      if (!this.videoAnalytics) return;
      const newTrack = event.newTrack;
      if (newTrack?.bandwidth) {
        this._reportBitrate(bitsToKbps(newTrack.bandwidth));
      }
      if (newTrack?.frameRate) {
        this.videoAnalytics.reportPlaybackMetric(Conviva.Constants.Playback.RENDERED_FRAMERATE, newTrack.frameRate);
      }
    };

    const onManifestParsed = () => {
      if (!this.videoAnalytics) return;
      const streamUrl = player.getAssetUri();
      const channel = streamUrl.match(/\/(ch\d+)\//)?.[1];
      const contentInfo: any = {
        [Conviva.Constants.DURATION]: player.getMediaElement().duration,
        [Conviva.Constants.STREAM_URL]: streamUrl,
      };
      if (channel) {
        contentInfo['c3.cm.channel'] = channel;
      }
      this.videoAnalytics.setContentInfo(contentInfo);
    };

    const onStarted = () => {
      if (!this.videoAnalytics) return;
      const tracks = player.getVariantTracks();
      const activeTrack = tracks.find((t: any) => t.active);
      if (activeTrack?.bandwidth) {
        this._reportBitrate(bitsToKbps(activeTrack.bandwidth));
      }
    };

    player.addEventListener('error', onError);
    player.addEventListener('variantchanged', onQualityChanged);
    player.addEventListener('manifestparsed', onManifestParsed);
    player.addEventListener('started', onStarted);

    this.cleanUpCallbacks.push(() => {
      player.removeEventListener('error', onError);
      player.removeEventListener('variantchanged', onQualityChanged);
      player.removeEventListener('manifestparsed', onManifestParsed);
      player.removeEventListener('started', onStarted);
    });
  }

  unsetPlayer() {
    this.player = null;
  }

  reportAppBackgrounded(): void {
    try {
      Conviva.Analytics.reportAppBackgrounded();
    } catch {
      // ignore
    }
  }

  reportAppForegrounded(): void {
    try {
      Conviva.Analytics.reportAppForegrounded();
    } catch {
      // ignore
    }
  }

  sendCustomEvent(eventName: string, attributes: Record<string, string>): void {
    if (!this.videoAnalytics) return;
    try {
      this.videoAnalytics.reportPlaybackEvent(eventName, attributes);
    } catch (error) {
      if (this.logging) {
        console.warn('[conviva][ShakaAdapter] Failed to send custom event', eventName, error);
      }
    }
  }

  getPlayerInfo(): PlayerInfo {
    return {
      name: 'ShakaPlayer',
      version: shaka.Player.version,
    };
  }

  setContentInfo(metadata: VideoMetadataType) {
    if (!this.videoAnalytics) return;

    const trimmedTitle = metadata.title?.trim() || '';
    const validAssetName = trimmedTitle && trimmedTitle.toUpperCase() !== 'NA' ? trimmedTitle : 'Unknown Asset';

    const streamUrl =
      this.player && typeof (this.player as any).getAssetUri === 'function' ? (this.player as any).getAssetUri() : '';

    const contentInfo: any = {
      [Conviva.Constants.IS_LIVE]: metadata.isLive ? 'LIVE' : 'VOD',
      [Conviva.Constants.ASSET_NAME]: validAssetName,
      [Conviva.Constants.VIEWER_ID]: String(metadata.viewerId) || 'NA',
    };
    if (streamUrl) {
      contentInfo[Conviva.Constants.STREAM_URL] = streamUrl;
    }
    if (metadata.custom) {
      Object.assign(contentInfo, metadata.custom);
    }
    this.videoAnalytics.setContentInfo(contentInfo);
  }
}
